import React from 'react';
import { OrderMarketTime, OrderTransaction, OrderType, OrderValidity } from '../TradingForm';
import { fieldNames } from './schema';
import DEFAULT_MODIFY_ORDER_FORM_TRANSLATIONS from './translations.json';
import { merge, cloneDeep, get } from 'lodash';
import { DeepPartial } from 'utility-types';
import { Leaves } from '../../types';
import { Currency } from '../../constants';
import { OrderWithExtraInfo } from '@orbis-systems/orbis-api-middleware';

export interface Order {
    orderType: OrderType;
    validity: OrderValidity;
    marketTime: OrderMarketTime;
    symbol: string;
    transaction: OrderTransaction;
    ourRef: string;
    mutualFund: boolean;
    option: boolean;
    orderLegs?: Order[];
    bond: boolean;
}

export type ModifyOrderFormOptions = {
    allowFractional: boolean;
    currency: string;
};

export const defaultModifyOrderFormOptions: ModifyOrderFormOptions = {
    allowFractional: false,
    currency: Currency.UsDollar,
};

export const getOrderMeta = (order: OrderWithExtraInfo | null) => {
    const stopOrder = order?.orderType === OrderType.Stop || order?.orderType === OrderType.StopLimit;
    const limitOrder = order?.orderType === OrderType.Limit || order?.orderType === OrderType.StopLimit;
    const optionTrading = order?.option || false;
    const spreadTrading = optionTrading && (order?.orderLegs || []).length > 1;
    const mutualFundTrading = order?.mutualFund || false;
    const trailingStopOrder = order?.orderType === OrderType.TrailingStop;
    const buying =
        order?.transaction === OrderTransaction.Buy ||
        order?.transaction === OrderTransaction.BuyToClose ||
        order?.transaction === OrderTransaction.BuyToCover ||
        order?.transaction === OrderTransaction.BuyToOpen;
    const selling =
        order?.transaction === OrderTransaction.Sell ||
        order?.transaction === OrderTransaction.SellToClose ||
        order?.transaction === OrderTransaction.SellToOpen ||
        order?.transaction === OrderTransaction.ShortSell;
    const bond = order?.bond;
    return {
        stopOrder,
        limitOrder,
        optionTrading,
        spreadTrading,
        mutualFundTrading,
        trailingStopOrder,
        buying,
        selling,
        bond,
    };
};

export type OrderMeta = ReturnType<typeof getOrderMeta>;

export { DEFAULT_MODIFY_ORDER_FORM_TRANSLATIONS };

export type ModifyOrderFormTranslations = DeepPartial<typeof DEFAULT_MODIFY_ORDER_FORM_TRANSLATIONS>;

export type ModifyOrderFormTranslationKey = Leaves<typeof DEFAULT_MODIFY_ORDER_FORM_TRANSLATIONS>;

export const createModifyOrderFormTranslateFunction = (translations?: ModifyOrderFormTranslations) => {
    const newTranslations = merge(cloneDeep(DEFAULT_MODIFY_ORDER_FORM_TRANSLATIONS), translations);
    return (key: ModifyOrderFormTranslationKey, values?: Record<string, unknown>) => {
        const value = get(newTranslations, key) as string | undefined;

        if (value === undefined) {
            throw new Error(`Translation not found for key \`${key}\``);
        }

        if (values) {
            return value.replace(/{{(.+)}}/g, (_match, token) => values[token] as string);
        }

        return value;
    };
};

export const ModifyOrderFormContext = React.createContext({
    fieldNames,
    options: defaultModifyOrderFormOptions,
    translate: createModifyOrderFormTranslateFunction(),
});
